import { useMutation, useQuery } from "@tanstack/react-query";
import { billingApi, type SubscriptionState } from "@/lib/api/billing";

export const billingKeys = {
  all: ["billing"] as const,
  subscription: () => [...billingKeys.all, "subscription"] as const,
};

/**
 * Reads the caller's subscription state.
 *
 * Two settings differ from the app's usual query defaults, both deliberate:
 *
 * - `retry: false`. Every other query retries because a failure is usually
 *   transient. Here a failure means the billing screen cannot render, and the
 *   screen must degrade to a usable "we could not load this" state promptly
 *   rather than spinning — a blocked customer is already stuck.
 *
 * - `staleTime` of one minute. Subscription status changes rarely and moves via
 *   Stripe webhooks, not user action, so refetching on every mount would put a
 *   request behind every navigation to serve a value that almost never changes.
 *   One minute is short enough that recovery after payment feels immediate.
 */
export function useSubscription() {
  return useQuery({
    queryKey: billingKeys.subscription(),
    queryFn: () => billingApi.subscription(),
    select: (response): SubscriptionState => response.data,
    staleTime: 60_000,
    retry: false,
  });
}

/**
 * The caller's access level, for gating UI affordances.
 *
 * Returns `full` while loading and on error. That is the deliberate direction:
 * this hook decides whether to *disable buttons*, not whether to permit
 * writes — the server decides that, and it has already refused anything it
 * should. Defaulting to restricted would flash a spurious read-only banner at
 * every paying customer on every cold load, which is a far more likely event
 * than the failure it would be guarding against.
 */
export function useEntitlement(): {
  access: EntitlementAccessLike;
  isReadOnly: boolean;
  isBillingOnly: boolean;
  isLoading: boolean;
} {
  const { data, isLoading } = useSubscription();
  const access = data?.access ?? "full";

  return {
    access,
    isReadOnly: access === "read-only",
    isBillingOnly: access === "billing-only",
    isLoading,
  };
}

type EntitlementAccessLike = SubscriptionState["access"];

/** Leave the app for a Stripe page. A seam so tests can observe it. */
export const stripeRedirect = {
  go: (url: string) => {
    window.location.assign(url);
  },
};

/** Send an org admin to Stripe Checkout (Team plan) or the billing portal. */
export function useBillingActions() {
  return {
    checkout: useMutation({
      mutationFn: () => billingApi.checkout(),
      onSuccess: (res) => {
        stripeRedirect.go(res.data.url);
      },
    }),
    portal: useMutation({
      mutationFn: () => billingApi.portal(),
      onSuccess: (res) => {
        stripeRedirect.go(res.data.url);
      },
    }),
  };
}
